import type { GridSize, PageMargins, Worksheet, WorksheetLayoutScales, WorksheetView } from '@jsfkit/types';
import { Document, Element } from '@borgar/simple-xml';
import { attr, boolAttr, numAttr } from '../utils/attr.ts';
import { rle } from '../utils/rle.ts';
import { handlerCell, relevantStyle } from './cell.ts';
import { ConversionContext } from '../ConversionContext.ts';
import type { Rel } from './rels.ts';
import { colWidth } from '../utils/colWidth.ts';
import { fromA1 } from '../utils/fromA1.ts';
import { toA1 } from '../utils/toA1.ts';
import { getFirstChild } from '../utils/getFirstChild.ts';
import { toInt } from '../utils/typecast.ts';
import { addProp } from '../utils/addProp.ts';
import { DEFAULT_PAGE_MARGINS } from '../constants.ts';

/**
 * Extracts zoom levels (layout scales) for the different view modes for a sheet.
 *
 * Excel stores separate zoom percentages for normal view, page layout view, and page break preview.
 * Only non-default zoom values are included in the returned object.
 */
function getLayoutScales (sheetView: Element): WorksheetLayoutScales | null {
  const scales: WorksheetLayoutScales = {};
  const normalScale = toInt(attr(sheetView, 'zoomScaleNormal'));
  if (normalScale != null) { scales.normal = normalScale; }
  const pageLayoutScale = toInt(attr(sheetView, 'zoomScalePageLayoutView'));
  if (pageLayoutScale != null) { scales.pageLayout = pageLayoutScale; }
  const pageBreakPreviewScale = toInt(attr(sheetView, 'zoomScaleSheetLayoutView'));
  if (pageBreakPreviewScale != null) { scales.pageBreakPreview = pageBreakPreviewScale; }
  return (normalScale ?? pageLayoutScale ?? pageBreakPreviewScale) != null ? scales : null;
}

function gridSize (start: number, end: number, size?: number | null, style?: number | null): GridSize {
  const item: GridSize = { start, end };
  if (size != null) {
    item.size = size;
  }
  if (style != null) {
    item.s = style;
  }
  return item;
}

export function handlerWorksheet (
  dom: Document,
  context: ConversionContext,
  rels: Rel[],
  sheetName: string,
): Worksheet {
  const sheet: Worksheet = {
    name: sheetName,
    cells: {},
    columns: [],
    rows: [],
    merges: [],
    defaults: {
      colWidth: colWidth(8, 5, context.normalMdw)!,
      rowHeight: 16,
    },
    // drawings: [],
    // showGridLines: true,
    hidden: context.sheetLinks.find(link => link.name === sheetName)?.hidden ?? 0,
  };

  // Store last selected cell and/or range (both optional) for each of the sheet's view. A sheet
  // view may be split into four panes, although of course most aren't. But to cover that case we
  // need to find the active pane then find its active cell. When there's only one pane (i.e. almost
  // all spreadsheets), you look for the default pane, "topLeft".
  const views: WorksheetView[] = [];
  const sheetViews = dom.querySelectorAll('sheetViews > sheetView');
  sheetViews.forEach(sheetView => {
    const view: WorksheetView = { workbookView: toInt(attr(sheetView, 'workbookViewId')) ?? 0 };
    const activeLayout = attr(sheetView, 'view');
    if (activeLayout === 'normal' || activeLayout === 'pageLayout' || activeLayout === 'pageBreakPreview') {
      view.activeLayout = activeLayout;
    }
    const pane = getFirstChild(sheetView, 'pane');
    const activePane = pane ? attr(pane, 'activePane', 'topLeft') : 'topLeft';
    const selection = sheetView.children
      .find(el => el.tagName === 'selection' && attr(el, 'pane', 'topLeft') === activePane);
    if (selection) {
      const activeCell = attr(selection, 'activeCell');
      if (activeCell) {
        view.activeCell = activeCell;
      }
      const activeRanges = attr(selection, 'sqref', '').trim().split(' ').filter(Boolean);
      if (activeRanges.length > 1 || activeRanges[0] !== activeCell) {
        view.activeRanges = activeRanges;
      }
    }
    addProp(view, 'showGridLines', boolAttr(sheetView, 'showGridLines'), true);
    addProp(view, 'layoutScales', getLayoutScales(sheetView));

    // Filter out views that contain only a workbook view id with no actual view state data. An id
    // alone isn't useful since it's just an index pointer. We only keep views that have at least
    // one piece of meaningful, non-default, data.
    if (Object.keys(view).length > 1) {
      views.push(view);
    }
  });

  if (views.length) {
    sheet.views = views;
  }

  // read hyperlinks
  const hyperLinks = new Map<string, string>();
  dom.querySelectorAll('hyperlinks > hyperlink').forEach(d => {
    const relId = attr(d, 'r:id');
    const rel = rels.find(item => item.id === relId);
    hyperLinks.set(attr(d, 'ref'), rel?.target ?? '');
  });

  // find default col/row sizes
  const sheetFormatPr = getFirstChild(dom.root, 'sheetFormatPr');
  if (sheetFormatPr) {
    const baseColWidthChars = numAttr(sheetFormatPr, 'baseColWidth', null);
    const defaultColWidthChars = numAttr(sheetFormatPr, 'defaultColWidth', null);
    sheet.defaults!.colWidth =
      colWidth(defaultColWidthChars, 0, context.normalMdw) ??
      colWidth(baseColWidthChars, 5, context.normalMdw) ??
      colWidth(8, 5, context.normalMdw)!;
    const rowHt = numAttr(sheetFormatPr, 'defaultRowHeight', null);
    if (rowHt != null) {
      sheet.defaults!.rowHeight = rowHt;
    }
  }

  // decode column widths (3.3.1.12)
  getFirstChild(dom.root, 'cols')?.children.forEach(d => {
    if (d.tagName !== 'col') { return; }
    const min = numAttr(d, 'min');
    const max = numAttr(d, 'max');
    if (min == null || max == null) { return; }
    const style = numAttr(d, 'style');
    const hidden = numAttr(d, 'hidden', 0);
    const size = colWidth(hidden ? 0 : numAttr(d, 'width'), 0, context.normalMdw);
    sheet.columns!.push(gridSize(min, max, size, style));
  });

  context._shared = new Map();
  context._arrayFormula = [];
  context._merged = {};

  // list merged cells
  getFirstChild(dom.root, 'mergeCells')?.children.forEach(d => {
    if (d.tagName !== 'mergeCell') { return; }
    const ref = attr(d, 'ref');
    if (ref) {
      const pRef = fromA1(ref);
      if (pRef) {
        const top = pRef.top ?? 0;
        const left = pRef.left ?? 0;
        const bottom = pRef.bottom ?? top;
        const right = pRef.right ?? left;
        const anchor = toA1(left, top);
        for (let c = left; c <= right; c++) {
          for (let r = top; r <= bottom; r++) {
            context._merged![toA1(c, r)] = anchor;
          }
        }
        sheet.merges!.push(ref);
      }
    }
  });

  // keep a list of row heights
  const rows: GridSize[] = [];

  // row id number is optional so we keep track of last used and auto-increment when id is missing.
  let lastR = 0;

  // parse cells
  getFirstChild(dom.root, 'sheetData')?.childNodes.forEach(row => {
    if (!(row instanceof Element) || row.tagName !== 'row') { return; }
    // .r = Row index. Indicates to which row in the sheet this
    //                 <row> definition corresponds.
    const r = numAttr(row, 'r', lastR + 1);

    // .hidden = 1 if the row is hidden
    // .ht = Row height measured in point size
    // .customFormat = 1 if the row style should be applied.
    // .s = Style Index. Index to style record for the row
    //                   (only applied if customFormat attribute is '1').
    const isHidden = numAttr(row, 'hidden');
    const rowStyle = numAttr(row, 's');
    if (isHidden) {
      rows.push(gridSize(r, r, 0, rowStyle));
    }
    else {
      // Row height measured in point size
      const ht = attr(row, 'ht');
      if (ht != null || rowStyle != null) {
        rows.push(gridSize(r, r, ht == null ? null : +ht, rowStyle));
      }
    }

    // cells: 3.3.1.3
    let lastId = '';
    row.childNodes.forEach(d => {
      if (!(d instanceof Element) || d.nodeName !== 'C') {
        return;
      }
      // the cell reference attribute is optional, but nearly always there
      let id = attr(d, 'r');
      if (!id) {
        // spec does not say what to do when the attribute is missing but
        // Excel will simply count from the last ID, so we do the same
        const cellPos = fromA1(lastId ?? 'A' + r)!;
        id = toA1((cellPos.left || 0) + 1, cellPos.top || 0);
      }
      const c = handlerCell(d, id, context);
      if (context.options.skipMerged && id) {
        if (context._merged![id] && context._merged![id] !== id) {
          // check if there are needed styles
          if (!c || !('s' in c) || (typeof c.s === 'number' && !relevantStyle(context.workbook!.styles![c.s]))) {
            // this cell is part of a merged range and has no required styles
            return;
          }
        }
      }
      if (c) {
        if (hyperLinks.has(id)) {
          c.l = hyperLinks.get(id);
        }
        sheet.cells[id] = c;
      }
      lastId = id;
    });
    lastR = r;
  });

  // run-length encode the row heights
  sheet.rows = rle(rows);

  // add .F tags to array formula cells
  context._arrayFormula.forEach(arrayRef => {
    const arrRef = fromA1(arrayRef);
    if (arrRef) {
      const top = arrRef.top ?? 0;
      const left = arrRef.left ?? 0;
      const bottom = arrRef.bottom ?? top;
      const right = arrRef.right ?? left;
      for (let r = top; r <= bottom; r++) {
        for (let c = left; c <= right; c++) {
          const ref = toA1(c, r);
          if (sheet.cells[ref]) {
            sheet.cells[ref].F = arrayRef;
          }
        }
      }
    }
  });

  // read page print margins
  const pm = getFirstChild(dom.root, 'pageMargins');
  if (pm) {
    const margins: PageMargins = {
      left: numAttr(pm, 'left'),
      right: numAttr(pm, 'right'),
      top: numAttr(pm, 'top'),
      bottom: numAttr(pm, 'bottom'),
      header: numAttr(pm, 'header'),
      footer: numAttr(pm, 'footer'),
    };
    if (
      // OOXML requires every attribute when `<pageMargins>` is present; if any are missing or
      // non-numeric, treat the element as absent rather than fabricating partial data.
      (margins.top != null && margins.bottom != null &&
      margins.left != null && margins.right != null &&
      margins.header != null && margins.footer != null) &&
      // Normalise a canonical-default element to absent so the common case keeps the JSF compact.
      (margins.top != DEFAULT_PAGE_MARGINS.top || margins.bottom != DEFAULT_PAGE_MARGINS.bottom ||
      margins.left != DEFAULT_PAGE_MARGINS.left || margins.right != DEFAULT_PAGE_MARGINS.right ||
      margins.header != DEFAULT_PAGE_MARGINS.header || margins.footer != DEFAULT_PAGE_MARGINS.footer)
    ) {
      sheet.pageMargins = margins;
    }
  }

  // detect linked drawing (graphics within the sheet)
  const drawing = getFirstChild(dom.root, 'drawing');
  if (drawing) {
    const rId = attr(drawing, 'r:id');
    const rel = rels.find(d => d.id === rId);
    if (rel) {
      context.images.push({ sheetName, rel, type: 'drawing' });
    }
  }

  // detect linked picture (sheet background "wallpaper")
  const picture = getFirstChild(dom.root, 'picture');
  if (picture) {
    const rId = attr(picture, 'r:id');
    const rel = rels.find(d => d.id === rId);
    if (rel) {
      context.images.push({ sheetName, rel, type: 'picture' });
    }
    // TODO: set a property on the sheet to link to this image
  }

  delete context._shared;
  delete context._arrayFormula;
  delete context._merged;
  return sheet;
}
